import { Icon } from '@blueprintjs/core'
import { Popover } from '../ui/popover'
import { Tooltip } from '../ui/tooltip'
import cx from 'classnames'
import { format, formatDistance } from 'date-fns'
import { observer } from 'mobx-react-lite'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearCurrentUser } from '../../actions/user'
import { useUser } from '../../hooks/useUser'
import type { Models } from '../../types/profile'
import { axiosPost } from '../../util/api'
import { Toasts } from '../toaster/Toast'
import { Rotate } from '../ui/Rotate'
type Profile = Models.Profile

interface UserSettingsComponentProps {
    isOpen: boolean
    setIsOpen: (v: boolean) => void
}

// This component gets used in the navbar.
// It's sole purpose is to give users
// access to settings, docs, resources, etc
const UserSettingsComponent = observer(
    ({ isOpen, setIsOpen }: UserSettingsComponentProps): JSX.Element => {
        const { user } = useUser()
        return (
            <Popover
                position="top-right"
                popoverClassName="userSettingsPopoverClass nav-bottom-popover border border-[#DBE2E8]"
                minimal
                content={
                    <UserSettingsComponentContent setIsOpen={setIsOpen} name={user?.name ?? ''} />
                }
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onOpened={() => setIsOpen(true)}
                modifiers={{
                    offset: {
                        enabled: true,
                        options: {
                            offset: [0, 12], // Adjust the vertical distance here (e.g., 10 pixels)
                        },
                    },
                }}
            >
                <div id="user-name-icon" onClick={() => setIsOpen(true)} className="relative">
                    <UserNameIcon name={user?.name ?? ''} />
                </div>
            </Popover>
        )
    },
)

interface UserSettingsComponentContent {
    setIsOpen: (v: boolean) => void
    name: string
}

const UserSettingsComponentContent = ({ setIsOpen, name }: UserSettingsComponentContent) => {
    const location = useLocation().pathname
    const [toggleSubMenu, setToggleSubMenu] = useState<string | undefined>(undefined)
    const subMenuRef = useRef<HTMLDivElement>(null)

    const handleClickOutsideSubMenu = (event: MouseEvent) => {
        // @ts-ignore
        if (
            subMenuRef.current &&
            !subMenuRef.current.contains(event.target as Node) &&
            event.target['textContent'] != 'Tools'
        ) {
            setToggleSubMenu(undefined)
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutsideSubMenu)
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideSubMenu)
        }
    }, [])

    return (
        <div
            style={{
                width: toggleSubMenu
                    ? 'calc(var(--mj-nav-v2-width, 256px) * 2)'
                    : 'var(--mj-nav-v2-width, 256px)',
            }}
            className={'flex rounded-lg bg-white overflow-hidden'}
        >
            {/* enable this when we have multiple users tied to acc */}
            {/* <div className="w-1/4 h-full py-2 px-3">
            <UsersMenu names={[name]} />
        </div> */}
            <div className={`${toggleSubMenu ? 'w-1/2' : 'w-full'} h-full`}>
                <Menu
                    setIsOpen={setIsOpen}
                    name={name}
                    setToggleSubMenu={setToggleSubMenu}
                    isSubMenuOpen={!!toggleSubMenu}
                />
            </div>
            {toggleSubMenu == 'Tools' && (
                <div
                    ref={subMenuRef}
                    style={{
                        borderLeft: '1px solid #DBE2E8',
                    }}
                    className="w-1/2 h-full"
                >
                    <ToolsMenuItems location={location} />
                </div>
            )}
        </div>
    )
}

const ToolsMenuItems = ({ location }: { location: string }) => {
    const navigate = useNavigate()
    const externalLink = (link: string) => {
        window.open(link, '_blank')
    }

    return (
        <ul className="px-3 py-2">
            <MenuItem onClick={navigate} text="Chat" location={location} route="chat" />
            <MenuItem
                onClick={navigate}
                text="Recipe Copilot"
                location={location}
                route="copilot"
            />
            <MenuItem
                onClick={navigate}
                text="Quick Create"
                location={location}
                route="tools/quick-create"
            />
            <MenuItem
                onClick={externalLink}
                text="Feature Requests"
                location={location}
                route="https://bit.ly/MJ-Feature-Idea"
            />
            <MenuItem
                onClick={navigate}
                text="Keyword Recommendations"
                location={location}
                route="tools/keyword-recommendations"
            />
            <MenuItem
                onClick={navigate}
                text="Bid Recommendations"
                location={location}
                route="tools/bid-recommendations"
            />
            <MenuItem
                onClick={navigate}
                text="Product Overview"
                location={location}
                route="tools/product-overview"
            />
        </ul>
    )
}

const UserNameIcon = ({ name, active = false }: { name: string; active?: boolean }) => {
    const mainIcon = (
        <div
            style={{ background: colorOptions[0] }}
            className="w-8 h-8 rounded-full flex items-center justify-center"
        >
            <span className="text-xs text-center text-white font-bold">{createAcronym(name)}</span>
        </div>
    )

    if (active) {
        return (
            <div
                style={{ border: '1px solid #3391FF', padding: 0.5 }}
                className="w-9 h-9 rounded-full flex items-center justify-center"
            >
                {mainIcon}
            </div>
        )
    }

    return mainIcon
}

interface MenuProps {
    setIsOpen: (v: boolean) => void
    name: string
    setToggleSubMenu: Dispatch<SetStateAction<string | undefined>>
    isSubMenuOpen: boolean
}

const Menu = ({ setIsOpen, name, setToggleSubMenu, isSubMenuOpen }: MenuProps) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const navigateToRoute = (route: string) => {
        navigate(route)
    }

    const logout = (): void => {
        axiosPost('logout', {}, true).then(() => {
            Toasts.success('Successfully logged out')
            dispatch(clearCurrentUser())
            navigate('/login')
            setIsOpen(false)
        })
    }

    const toggleSubMenu = (name: string) => {
        setToggleSubMenu((prev) => {
            if (prev == name) {
                return undefined
            }

            return name
        })
    }

    const toggleGistChat = () => {
        // if (gistOpen) {
        //     window['gist'].chat('close')
        //     window['gist'].chat('hide')
        // } else {
        window['gist'].chat('open')
        window['gist'].chat('showLauncher')
        // }
    }

    const onClickTools = () => {
        toggleSubMenu('Tools')
    }

    const toolsChevron = () => (
        <Rotate className="ml-2">
            <Icon className="pt-1" icon="chevron-left" color="#A6B0BC" />
        </Rotate>
    )

    return (
        <div className="px-3 py-2 h-full bg-white">
            <div style={{ paddingTop: 0.5 }} className="mb-3">
                <MenuHeader name={name} />
            </div>
            <ul className="cursor-pointer">
                <li onClick={() => navigateToRoute('keys')} className="hover:bg-mjGray-300">
                    Amazon Ads Connection
                </li>
                <li onClick={() => navigateToRoute('api-keys')} className="hover:bg-mjGray-300">
                    API Keys
                </li>
                <li onClick={() => navigateToRoute('settings')} className="hover:bg-mjGray-300">
                    Data Connections
                </li>
                {/* <li onClick={() => navigateToRoute('integrations')} className="hover:bg-mjGray-300">Integrations</li>*/}
                <li onClick={() => navigateToRoute('billing')} className="hover:bg-mjGray-300">
                    Billing
                </li>
                <li
                    onClick={() => navigateToRoute('profile-settings')}
                    className="hover:bg-mjGray-300"
                >
                    Settings
                </li>
                <li onClick={logout} className="hover:bg-mjGray-300">
                    Log Out
                </li>
                <hr className="my-2" />
                <li onClick={onClickTools} className="hover:bg-mjGray-300 flex items-center">
                    <span>Other Tools</span>
                    {toolsChevron()}
                </li>
                <li onClick={toggleGistChat} className="hover:bg-mjGray-300">
                    Get Support
                </li>
                <li
                    onClick={() => window.open('https://merchjar.com/help-center', '_blank')}
                    className="hover:bg-mjGray-300"
                >
                    Documentation
                </li>
            </ul>
        </div>
    )
}

export const MenuHeader = ({ name }: { name: string }) => {
    return (
        <div className="h-full flex items-center">
            <div className="mt-0 pt-0 w-1/4 cursor-default">
                <UserNameIcon name={name} />
            </div>
            <div
                style={{ color: '#2B4E75' }}
                className="w-3/4 text-sm font-semibold font-normal flex items-center cursor-default"
            >
                {name}
            </div>
        </div>
    )
}

interface NavbarBottomSectionProps {
    activeProfile?: Profile
    profiles: Profile[]
}

export const NavbarBottomSection = ({
    activeProfile = undefined,
    profiles = [],
}: NavbarBottomSectionProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const userHasManagedProfiles = useMemo(() => {
        if (profiles?.length > 0) {
            for (let i = 0; i < profiles.length; i++) {
                const p = profiles[i]
                if (p.managed) {
                    return true
                }
            }
        }
    }, [profiles])

    const noDataSyncPerformedVerbiage = (): string => {
        if (profiles?.length == 0) {
            return 'No ad accounts linked to your account'
        }

        if (activeProfile?.is_initial_data_sync) {
            return 'First data sync in progress..'
        }

        // if user has profiles, but are not managed, then the users needs to manage a profile
        // to enable data syncs
        if (!userHasManagedProfiles) {
            return 'Manage an ad account to start data syncs'
        }

        return 'Data sync in progress'
    }

    // An optional relative human diff, like "2 hours ago"
    let lastUpdatedHuman
    if (activeProfile?.update_info?.last_updated_at) {
        lastUpdatedHuman = formatDistance(
            new Date(),
            new Date(activeProfile.update_info.last_updated_at),
        ).replace('about ', '')
    }

    return (
        <div
            className="navbar-bottom-section h-full flex items-center mr-px"
            style={{
                borderTop: '1px solid #DBE2E8',
                // borderRight: '1px solid #DBE2E8'
            }}
        >
            <div className="ml-3 w-1/4 cursor-pointer flex">
                <div className="pr-0.5">
                    <UserSettingsComponent isOpen={isOpen} setIsOpen={setIsOpen} />
                </div>
                <Rotate className="mt-2" style={{ width: 15, height: 15 }}>
                    <Icon
                        className={isOpen ? 'pointer-events-none' : undefined}
                        onClick={() => setIsOpen(!isOpen)}
                        icon={'chevron-down'}
                        color="#A6B0BC"
                    />
                </Rotate>
            </div>
            <div
                id="last-sync-time"
                style={{ color: '#2B4E75' }}
                className="w-3/4 text-xs font-normal flex items-center"
            >
                {activeProfile?.update_info?.last_updated_at ? (
                    <Tooltip
                        content={format(
                            new Date(activeProfile.update_info.last_updated_at),
                            'M/d/yy H:mm a',
                        )}
                    >
                        <span>Updated {lastUpdatedHuman} ago</span>
                    </Tooltip>
                ) : (
                    <span>{noDataSyncPerformedVerbiage()}</span>
                )}
            </div>
        </div>
    )
}

function createAcronym(text: string, maxLetters: number = 2): string {
    const words = text.split(' ')

    let output = ''
    words.forEach((word) => {
        output += word.charAt(0)
    })

    return output.substring(0, maxLetters).toUpperCase()
}

const colorOptions = ['#E233FF', '#FFC833', '#33DAFF']

// This component supports:
// - highlight on active item
// - go to location on click
export const MenuItem = ({ onClick, text, route, location }: MenuItemProps) => {
    // remove the first "/"
    location = location.substring(1)
    if (location.includes('/')) {
        // get the parent route
        if (location.split('/')[0] != 'tools') {
            location = location.split('/')[0]
        }
    }

    return (
        <li
            className={cx('hover:bg-mjGray-300 cursor-pointer pl-1.5', {
                itemSelected: location == route,
            })}
            onClick={() => onClick(route)}
        >
            {text}
        </li>
    )
}

interface MenuItemProps {
    onClick: (route: string) => void
    text: string
    route: string
    location: string
}
